/**
 * `ConditionBuilder` — design §4 step 8, the condition builder: up to `MAX_CLAUSES` (4) rows
 * of `{layer, op, value}`, two predefined scenario buttons from `GET uc3/config`'s own
 * `scenarios.A`/`.B`, and `#uc3Run` which submits the `conditions` tool through the
 * `useToolRun` instance `Uc3.tsx` owns (so the results step can read the same `run.result`).
 *
 * The layer list is the ruling's own ten, four of them parameterised by the reader's chosen
 * crop: `mask:<c>` (crop area), `yield:<c>` (survey tercile), `gaez:<c>` (GAEZ round: the
 * crop's modelled yield, t/ha) and `gaez_gap:<c>` (modelled yield gap), plus the six
 * crop-independent ones — `ndvi_peak`, `rain_season`, `soc`, `ph`, `sgcec`, `sgn` —
 * `backend/apps/analysis/tools/conditions.py`'s own vocabulary (`_inputs`/`_clause_mask`),
 * not a client invention. The value input's shape follows the op, mirroring
 * `conditions.py::_validate_value` exactly so a row this component lets through is never
 * refused for its shape: `lt`/`gt` take one number, `between` two, `class_in` a
 * comma-separated list of integers, `tercile` one or more of low/medium/high (checkboxes,
 * joined with `|` — scenario B's own "medium or high" rainfall clause).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ConditionClause } from '../../lib/analysis';
import type { ToolRun, ConditionsResult } from '../../lib/analysis';
import type { IntelUc3Config } from '../../lib/api';
import { Icon } from '../../lib/icons';
import { ProgressBar } from '../analysis/ProgressBar';
import { TIMED_OUT } from '../../lib/analysis';

const MAX_CLAUSES = 4;

/** The six clause layers `conditions.py` reads the same way regardless of crop. */
const CROP_INDEPENDENT_LAYERS = ['ndvi_peak', 'rain_season', 'soc', 'ph', 'sgcec', 'sgn'] as const;

/**
 * `conditions.py`'s own layer vocabulary for the current crop — see the module header. The
 * four crop-parameterised aliases first (matching the ruling's own order), then the six
 * crop-independent ones.
 */
function layerOptionsFor(crop: string): string[] {
  return [`mask:${crop}`, `yield:${crop}`, `gaez:${crop}`, `gaez_gap:${crop}`, ...CROP_INDEPENDENT_LAYERS];
}

const OP_OPTIONS = ['lt', 'gt', 'between', 'class_in', 'tercile'] as const;

const TERCILE_CLASSES = ['low', 'medium', 'high'] as const;

function defaultValueFor(op: (typeof OP_OPTIONS)[number]): unknown {
  switch (op) {
    case 'between':
      return [0, 1];
    case 'class_in':
      return [1];
    case 'tercile':
      return 'low';
    default:
      return 0;
  }
}

function emptyClause(crop: string): ConditionClause {
  return { layer: layerOptionsFor(crop)[0]!, op: 'gt', value: 0 };
}

/**
 * A clause layer's own label — `mask:<c>`/`yield:<c>`/`gaez:<c>`/`gaez_gap:<c>` carry the
 * crop's display label through `{{crop}}` interpolation (`intel.uc3.layer.mask`, `.yield`,
 * `.gaez`, `.gaez_gap`); the six crop-independent layers keep their own static key exactly
 * as before (`intel.uc3.layer.ndvi_peak`, …).
 */
function layerLabel(t: (key: string, opts?: Record<string, unknown>) => string, layer: string, cropLabel: string): string {
  const sep = layer.indexOf(':');
  if (sep === -1) return t(`intel.uc3.layer.${layer}`, { defaultValue: layer });
  const kind = layer.slice(0, sep);
  return t(`intel.uc3.layer.${kind}`, { crop: cropLabel, defaultValue: layer });
}

/**
 * Fix round 1: mirrors `conditions.py::_validate_value` so a row this builder would submit
 * is never one the backend refuses for its shape — and, the other direction, so a shape the
 * backend *would* refuse (an emptied-out `tercile` row, in particular) is caught here first,
 * inline, rather than as a 422 after the reader has already pressed Run.
 */
function isClauseValid(clause: ConditionClause): boolean {
  const { op, value } = clause;
  if (op === 'lt' || op === 'gt') return typeof value === 'number' && Number.isFinite(value);
  if (op === 'between') {
    return (
      Array.isArray(value) &&
      value.length === 2 &&
      value.every((v) => typeof v === 'number' && Number.isFinite(v)) &&
      (value as number[])[0]! <= (value as number[])[1]!
    );
  }
  if (op === 'class_in') {
    return Array.isArray(value) && value.length > 0 && value.every((v) => Number.isInteger(v));
  }
  // tercile
  if (typeof value !== 'string' || !value) return false;
  return value.split('|').every((part) => (TERCILE_CLASSES as readonly string[]).includes(part));
}

export interface ConditionBuilderProps {
  config?: IntelUc3Config;
  /** The reader's chosen crop id — builds this crop's own `mask:<c>`/`yield:<c>`/… aliases. */
  crop: string;
  /** The crop's display label in the current language, for the four crop-parameterised layer labels. */
  cropLabel: string;
  run: ToolRun<ConditionsResult>;
  onRun: (clauses: ConditionClause[]) => void;
}

export function ConditionBuilder({ config, crop, cropLabel, run, onRun }: ConditionBuilderProps) {
  const { t } = useTranslation();
  const [clauses, setClauses] = useState<ConditionClause[]>(() => [emptyClause(crop)]);
  const layerOptions = layerOptionsFor(crop);

  // A crop switch invalidates every crop-parameterised layer alias a row may be holding
  // (`mask:maize` means nothing once the reader has moved to sorghum) — starting the builder
  // over on the new crop's own default row is simpler and safer than trying to remap each
  // row's layer in place.
  useEffect(() => {
    setClauses([emptyClause(crop)]);
  }, [crop]);

  const updateClause = (i: number, patch: Partial<ConditionClause>) =>
    setClauses((rows) => rows.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const setOp = (i: number, op: (typeof OP_OPTIONS)[number]) =>
    updateClause(i, { op, value: defaultValueFor(op) });

  const addRow = () =>
    setClauses((rows) => (rows.length >= MAX_CLAUSES ? rows : [...rows, emptyClause(crop)]));

  const removeRow = (i: number) => setClauses((rows) => rows.filter((_, idx) => idx !== i));

  const fillScenario = (key: 'A' | 'B') => {
    const scenario = config?.scenarios?.[key];
    if (!scenario) return;
    setClauses(scenario.clauses.map((c) => ({ ...c })) as ConditionClause[]);
  };

  const submit = () => onRun(clauses);

  const invalid = clauses.some((c) => !isClauseValid(c));

  const message = run.error === TIMED_OUT ? t('analysis.timedOut') : run.error;

  return (
    <div className="uc3-builder">
      <div className="ad-actions">
        <button type="button" className="btn ghost sm" id="uc3Scenario-A" onClick={() => fillScenario('A')}>
          {t('intel.uc3.scenarioA')}
        </button>
        <button type="button" className="btn ghost sm" id="uc3Scenario-B" onClick={() => fillScenario('B')}>
          {t('intel.uc3.scenarioB')}
        </button>
      </div>

      <div className="uc3-clauses" id="uc3Clauses">
        {clauses.map((clause, i) => (
          <div className="uc3-clause" key={i} data-testid={`uc3-clause-${i}`}>
            <select
              aria-label={t('intel.uc3.layerLabel')}
              value={clause.layer}
              onChange={(e) => updateClause(i, { layer: e.target.value })}
            >
              {layerOptions.map((layer) => (
                <option key={layer} value={layer}>
                  {layerLabel(t, layer, cropLabel)}
                </option>
              ))}
            </select>
            <select
              aria-label={t('intel.uc3.opLabel')}
              value={clause.op}
              onChange={(e) => setOp(i, e.target.value as (typeof OP_OPTIONS)[number])}
            >
              {OP_OPTIONS.map((op) => (
                <option key={op} value={op}>
                  {t(`intel.uc3.op.${op}`)}
                </option>
              ))}
            </select>
            <ClauseValue clause={clause} onChange={(value) => updateClause(i, { value })} />
            {clauses.length > 1 ? (
              <button
                type="button"
                className="btn ghost sm"
                aria-label={t('intel.uc3.removeClause')}
                onClick={() => removeRow(i)}
              >
                ✕
              </button>
            ) : null}
          </div>
        ))}
      </div>

      <button
        type="button"
        className="btn ghost sm"
        id="uc3AddClause"
        disabled={clauses.length >= MAX_CLAUSES}
        onClick={addRow}
      >
        {t('intel.uc3.addClause')}
      </button>

      {invalid ? (
        <div className="note" id="uc3ClauseInvalid" role="alert">
          {t('intel.uc3.invalidClause')}
        </div>
      ) : null}

      <button
        type="button"
        className="btn primary ad-run"
        id="uc3Run"
        disabled={run.phase === 'running' || invalid}
        onClick={submit}
      >
        <Icon name="play" size={13} /> {t('intel.uc3.run')}
      </button>

      {run.phase === 'running' ? (
        <ProgressBar value={run.progress} label={t('analysis.progress')} />
      ) : null}
      {run.phase === 'error' ? (
        <div className="note" role="alert">
          {message ?? t('analysis.failed')}
        </div>
      ) : null}
    </div>
  );
}

function ClauseValue({
  clause,
  onChange,
}: {
  clause: ConditionClause;
  onChange: (value: unknown) => void;
}) {
  const { t } = useTranslation();

  if (clause.op === 'lt' || clause.op === 'gt') {
    return (
      <input
        type="number"
        value={typeof clause.value === 'number' ? clause.value : 0}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    );
  }

  if (clause.op === 'between') {
    const [lo, hi] = Array.isArray(clause.value) ? clause.value : [0, 1];
    return (
      <span className="uc3-between">
        <input type="number" value={lo} onChange={(e) => onChange([Number(e.target.value), hi])} />
        {' – '}
        <input type="number" value={hi} onChange={(e) => onChange([lo, Number(e.target.value)])} />
      </span>
    );
  }

  if (clause.op === 'class_in') {
    const text = Array.isArray(clause.value) ? clause.value.join(',') : '';
    return (
      <input
        type="text"
        placeholder="1,2,3"
        defaultValue={text}
        onBlur={(e) =>
          onChange(
            e.target.value
              .split(',')
              .map((s) => Number(s.trim()))
              .filter((n) => Number.isFinite(n)),
          )
        }
      />
    );
  }

  // tercile — fix round 1: unchecking the last ticked class now leaves the value empty
  // (`''`) rather than snapping back to the class just unticked. An empty value is not a
  // clause `conditions.py` would accept, so it is caught by `isClauseValid` and surfaced as
  // the builder's own inline "pick at least one" message with Run disabled — never silently
  // overridden back to a state the reader just asked to leave.
  const picked = new Set(
    typeof clause.value === 'string' && clause.value ? clause.value.split('|') : [],
  );
  const toggle = (cls: string) => {
    const next = new Set(picked);
    if (next.has(cls)) next.delete(cls);
    else next.add(cls);
    onChange(TERCILE_CLASSES.filter((c) => next.has(c)).join('|'));
  };
  return (
    <span className="uc3-tercile">
      {TERCILE_CLASSES.map((cls) => (
        <label key={cls}>
          <input type="checkbox" checked={picked.has(cls)} onChange={() => toggle(cls)} />
          {t(`intel.uc3.tercile.${cls}`)}
        </label>
      ))}
    </span>
  );
}

export default ConditionBuilder;
